import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NumPadKeys } from "./NumPadKeys";
import { blurOtherField } from "./padFocus";
import css from "./PhonePad.module.css";

/**
 * PhonePad — телефонный ПОПОВЕР у поля (A46-дополнение 2026-08-13; был шитом
 * БМ3). Механика — NumPadPop (M.5) 1-в-1: портал в body, place() с флипом
 * вверх и клампом, синглтон (открытие нового гасит предыдущий БЕЗ commit),
 * тап МИМО = commit, Esc = отмена, ✓ = commit, скролл вне = «тап мимо»
 * (грейс SCROLL_GRACE_MS), программное open=false хостом = отмена БЕЗ commit.
 * Но КРУПНЕЕ M.5 («чтобы не промахнуться»): ширина ~210px, клавиши ~48px.
 *
 * СВОБОДНАЯ модель (решение владельца 15.08, отменяет жёсткую маску БМ3/A46+):
 * значение — сырая строка как ввели. Пустое поле открывается с предзаполненным
 * «+79» — его МОЖНО стереть (⌫ посимвольно, long-press 480мс — всё). Цифры
 * дописываются в конец; «Вставить» кладёт буфер КАК ЕСТЬ (без отбраковки
 * городских и обрезки). Канонизацию в вид БД («8»+10) делает бэк
 * (normalizePhone) при сохранении. Fresh: первая цифра поверх существующего
 * номера начинает заново с «+79»+цифра. Без единого нажатия (touchedRef) —
 * исходная строка value байт-в-байт (открыл-закрыл ничего не портит).
 */
export interface PhonePadProps {
  open: boolean;
  /** Якорное поле телефона (обычно e.currentTarget тапа, M.5). */
  anchor: HTMLElement | null;
  label?: string;
  /** Сырая строка номера (канон БД «89…» у существующих броней — как есть). */
  value?: string;
  /** Тап мимо / ✓; «изменилось ли» диффает хост. Зовётся ПОСЛЕ onClose. */
  onCommit?: (v: string) => void;
  /** Хост сбрасывает open; при Esc — единственный вызов (без commit). */
  onClose: () => void;
  /** Поверх экранных модалок (уровень NumPadPop). */
  zIndex?: number | string;
}

/** Предзаполнение пустого поля — стираемое (решение 15.08). */
const PREFILL = "+79";
const RAW_MAX = 24;
const DEL_LP_MS = 480;
const HINT_MS = 1400;
/** Грейс скролла после открытия — как NumPadPop (инерция/PTR-резина). */
const SCROLL_GRACE_MS = 150;

/* Синглтон (паттерн NumPadPop): открытие нового гасит предыдущий без commit. */
let activeCancel: (() => void) | null = null;

const IcoClip = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="8" y="2" width="8" height="4" rx="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
  </svg>
);

export function PhonePad(props: PhonePadProps) {
  const { open, anchor, zIndex = "calc(var(--z-modal) + 60)" } = props;
  const [raw, setRaw] = useState("");
  const [fx, setFx] = useState(0);
  const [hint, setHint] = useState({ text: "", on: false });
  const [entered, setEntered] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const doneRef = useRef(true);
  const freshRef = useRef(false);
  const touchedRef = useRef(false);
  const hintT = useRef(0);
  const delT = useRef(0);
  const delFired = useRef(false);
  const rawRef = useRef("");
  const propsRef = useRef(props);
  propsRef.current = props;

  /* Единственная точка записи: реф свежий СИНХРОННО (быстрые тапы до
     ре-рендера не теряют цифры) — StrictMode-чистота (BM3-fixer). */
  const setRawNow = (next: string) => {
    rawRef.current = next;
    setRaw(next);
  };

  /* Закрытие ровно один раз (doneRef); commit — только тап мимо / ✓. */
  const close = (commit: boolean) => {
    if (doneRef.current) return;
    doneRef.current = true;
    const p = propsRef.current;
    p.onClose();
    if (!commit) return;
    /* Ни одного нажатия — исходное value как есть (нетронутый префилл не утекает). */
    if (!touchedRef.current) return p.onCommit?.(String(p.value ?? ""));
    p.onCommit?.(rawRef.current);
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  /* Пересброс на открытие/смену якоря: value как есть либо префилл, fresh. */
  useEffect(() => {
    if (!open) return;
    activeCancel?.();
    const mine = () => closeRef.current(false);
    activeCancel = mine;
    doneRef.current = false;
    blurOtherField(); /* 06.09 (владелец): одна клавиатура за раз — фокус (и клавиатура ОС) уходит из чужого поля */
    const v = String(propsRef.current.value ?? "");
    setRawNow(v === "" ? PREFILL : v);
    /* 06.09 (оператор Даша: «при переключении сбрасывается номер»): fresh-старт
       «+79»+цифра — только если в поле уже ПОЛНЫЙ номер (≥11 цифр, канон из брони).
       Недобитый номер (закрыл пад, посмотрел вейвер, открыл снова) — дописываем. */
    freshRef.current = v.replace(/\D/g, "").length >= 11;
    touchedRef.current = false;
    setFx(0);
    setHint({ text: "", on: false });
    return () => {
      if (activeCancel === mine) activeCancel = null;
    };
  }, [open, anchor]);

  /* place() NumPadPop: left-кламп 6px, вниз от якоря +4px → вверх → кламп. */
  useLayoutEffect(() => {
    if (!open) return;
    const pop = popRef.current;
    if (!pop || !anchor) return;
    const ar = anchor.getBoundingClientRect();
    const ph = pop.offsetHeight;
    const pw = pop.offsetWidth;
    const left = Math.max(6, Math.min(ar.left, window.innerWidth - pw - 6));
    const spaceDown = window.innerHeight - ar.bottom - 8;
    const spaceUp = ar.top - 8;
    let top: number;
    if (ph <= spaceDown) top = ar.bottom + 4;
    else if (ph <= spaceUp) top = ar.top - ph - 4;
    else top = Math.max(6, Math.min(ar.bottom + 4, window.innerHeight - ph - 6));
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }, [open, anchor]);

  /* Появление 130мс — класс .on через rAF (как NumPadPop). */
  useEffect(() => {
    if (!open) return setEntered(false);
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  /* Тап мимо = commit (setTimeout(0) пропускает открывший тап), Esc capture =
     отмена, скролл вне = «тап мимо» с грейсом — слушатели NumPadPop 1-в-1. */
  useEffect(() => {
    if (!open) return;
    const t0 = performance.now();
    const inPop = (e: Event) => e.target instanceof Node && popRef.current?.contains(e.target);
    const onDown = (e: globalThis.PointerEvent) => { if (!inPop(e)) closeRef.current(true); };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); closeRef.current(false); }
    };
    const onScroll = (e: Event) => { if (performance.now() - t0 >= SCROLL_GRACE_MS && !inPop(e)) closeRef.current(true); };
    const t = window.setTimeout(() => {
      document.addEventListener("pointerdown", onDown, true);
      document.addEventListener("scroll", onScroll, true);
    }, 0);
    document.addEventListener("keydown", onKey, true);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  /* Инлайн-хинт на HINT_MS; shake превью — по флагу. */
  const note = (text: string, shake: boolean) => {
    if (shake) setFx((n) => n + 1);
    setHint({ text, on: true });
    window.clearTimeout(hintT.current);
    hintT.current = window.setTimeout(() => setHint((h) => ({ ...h, on: false })), HINT_MS);
  };

  const append = (k: string) => {
    const base = freshRef.current ? PREFILL : rawRef.current;
    freshRef.current = false;
    if (base.length >= RAW_MAX) return setFx((n) => n + 1); // полон — shake (не touch)
    touchedRef.current = true;
    setRawNow(base + k);
  };

  const delDown = () => {
    delFired.current = false;
    delT.current = window.setTimeout(() => { delFired.current = true; freshRef.current = false; touchedRef.current = true; setRawNow(""); }, DEL_LP_MS);
  };
  const delUp = () => window.clearTimeout(delT.current);
  const delClick = () => {
    if (delFired.current) { delFired.current = false; return; } // long-press глушит свой click
    freshRef.current = false;
    touchedRef.current = true;
    setRawNow(rawRef.current.slice(0, -1));
  };

  /* «Вставить»: буфер КАК ЕСТЬ (15.08) — без нормализации и отбраковки. */
  const paste = async () => {
    let txt = "";
    try { txt = await navigator.clipboard.readText(); } catch { return note("буфер недоступен", false); }
    const t = txt.trim();
    if (t === "") return note("буфер пуст", true);
    freshRef.current = false; touchedRef.current = true;
    setRawNow(t.slice(0, RAW_MAX));
    if (t.length > RAW_MAX) note("обрезано", true);
  };

  if (!open || !anchor) return null;
  return createPortal(
    <div className={css.host} style={{ zIndex }}>
      <div ref={popRef} className={`${css.pop} ${entered ? css.on : ""}`} role="dialog" aria-label={props.label ?? "Телефон"}>
        <div className={css.head}>
          <span className={css.label}>{props.label ?? "Телефон"}</span>
          <span key={fx} className={`${css.mask} ${fx ? css.shk : ""}`}>
            {raw !== "" ? <span className={css.done}>{raw}</span> : <span className={css.rest}>+79…</span>}
          </span>
        </div>
        <div className={`${css.hint} ${hint.on ? css.on : ""}`}>{hint.text}</div>
        <button type="button" className={css.paste} onClick={() => void paste()}>
          <IcoClip />
          <span>Вставить</span>
        </button>
        <NumPadKeys
          styles={{ keys: css.keys!, row: css.row!, key: css.key!, del: css.del!, okk: css.okk!, fl: css.fl! }}
          onDigit={append}
          onOk={() => close(true)}
          onDelDown={delDown}
          onDelUp={delUp}
          onDelClick={delClick}
        />
      </div>
    </div>,
    document.body,
  );
}
